/**
 * Customized for React app.
 * Save the response data into local storage.
 */

import { DEFAULT_LANGUAGE } from '@/data/config/DEFAULTS';
import { COOKIE_GDPR_MANAGEMENT } from '@/data/constants/privacyPolicy';
import { setDefaultRequest } from '@/data/Content/DefaultStore';
import { WC_TOKEN, WC_TRUSTED_TOKEN } from '@/data/forReactAndMobile/constants';
import {
	clearCurrentUserFromStorage,
	getCurrentUserFromStorage,
	saveCurrentUserToStorage,
} from '@/data/forReactAndMobile/SaveCurrentUser';
import { getCookieName } from '@/data/utils/getCookieName';
import { getRequestId } from '@/data/utils/getRequestId';
import { errorWithId } from '@/data/utils/loggerUtil';
import { RequestParams } from 'integration/generated/transactions/http-client';
import transactionsGuestIdentity from 'integration/generated/transactions/transactionsGuestIdentity';
import { GetServerSidePropsContext } from 'next';
import { Cookies } from 'react-cookie';
import {
	DEFAULT_STORE_ID,
	GEO_LOCATION_COOKIE_NAME,
	MIN_EXP_TIME,
} from 'shared/custom/constants/commonConstants';

export const guestIdentityLoginFetcher =
	(pub: boolean, context?: GetServerSidePropsContext) =>
	async (storeId: string, params: RequestParams) => {
		try {
			const currentUserStorage = getCurrentUserFromStorage(
				process.env.NEXT_PUBLIC_REACT_APP_ROUTER_BASENAME || ''
			);
			// Remove local storage data because if this request is executed is due to trying to get a new guest session.
			if (currentUserStorage?.WCToken) {
				clearCurrentUserFromStorage(process.env.NEXT_PUBLIC_REACT_APP_ROUTER_BASENAME || '');
			}
			const cookies = new Cookies();
			const _data = COOKIE_GDPR_MANAGEMENT.reduce((acc, name) => {
				const cValue = cookies.get(getCookieName({ name, storeId }));
				return { ...acc, ...(cValue !== undefined && { [name]: cValue }) };
			}, {});
			const resp = await transactionsGuestIdentity(true).guestIdentityLogin(
				storeId,
				{
					updateCookies: true,
				} as any,
				_data,
				params
			);
			const cookieValue = cookies.get(GEO_LOCATION_COOKIE_NAME) ?? 'defaultStore';
			const defaultStorePayload = {
				defaultStore: cookieValue?.stLocId ?? DEFAULT_STORE_ID,
				fromPageName: '2',
			};
			await setDefaultRequest(defaultStorePayload, storeId, DEFAULT_LANGUAGE, MIN_EXP_TIME, {
				headers: {
					[WC_TOKEN]: resp.WCToken,
					[WC_TRUSTED_TOKEN]: resp.WCTrustedToken,
				},
			});
			const currentUser = {
				...resp,
				userLoggedIn: false,
				isGuest: true,
				generatedCookie: true,
				rememberMe: true,
				newGuestToken: true,
			};
			saveCurrentUserToStorage(
				process.env.NEXT_PUBLIC_REACT_APP_ROUTER_BASENAME || '',
				currentUser
			);
			return resp;
		} catch (e) {
			errorWithId(getRequestId(context), 'GuestFetcher: guestIdentityLoginFetcher: error: ', {
				error: e,
			});
			if (pub) {
				throw e;
			}
			return undefined;
		}
	};

export const guestIdentityLogoutFetcher =
	(pub: boolean, context?: GetServerSidePropsContext) =>
	async (storeId: string, params: RequestParams) => {
		try {
			const resp = await transactionsGuestIdentity(true).guestIdentityLogout(
				storeId,
				{
					updateCookies: true,
				} as any,
				params
			);
			clearCurrentUserFromStorage(process.env.NEXT_PUBLIC_REACT_APP_ROUTER_BASENAME || '');
			return resp;
		} catch (e) {
			errorWithId(getRequestId(context), 'GuestFetcher: guestIdentityLogoutFetcher: error: ', {
				error: e,
			});
			if (pub) {
				throw e;
			}
			return undefined;
		}
	};
